import Polyglot from 'node-polyglot';
import {
  contactVariables,
  distributionListSubjectVariables,
  distributionListBodyVariables,
  deliveryTabBodyVariables,
  deliveryTabSubjectVariables
} from '../../features/resourceuploads/uploadsteps/emailDialog/EmailFormLookups';
import moment from 'moment';
import axios from 'axios';
import ApiBase from '../apis/ApiBase';

const DEFAULT_CHUNK_PARAMS_MAX_SIZE = 3000; // 3000 characters max in a GET request to limit the URI size

/**
 * Return True if the application is runing in a development evn.
 */
export function isDevMode() {
  return (
    !process.env.NODE_ENV ||
    process.env.NODE_ENV === 'development' ||
    process.env.REACT_APP_LOCAL_BUILD
  );
}

/**
 * Return the main path name for TabbedForm. ex: investors/inv9456/1  returns =>  investors/inv9456
 * @param {*} path
 */
export function removePathEnd(path) {
  const reg = /^(\/[^/]+\/[^/]+)\/[\d]+$/.exec(path);
  if (reg) {
    return reg[1];
  }
  return path;
}

export function getInitials(name) {
  const initials = name.match(/\b(\w)/g);
  return initials !== null && initials.length > 1
    ? initials
        .join('')
        .substring(0, 2)
        .toUpperCase()
    : name.length > 1
    ? name.substring(0, 2).toUpperCase()
    : name.toUpperCase();
}

/**
 * Return the string message prepared for usage in showNotification() function.
 * The purpose of this method is to prepare string message with pluralization.
 * Since we cannot use notify (hooks) in Class Component this method will provide
 * pluralization for message which can be used in showNotification() function.
 * The function parameter message should be formated like this:
 * 'Changes were saved successfully |||| %{smart_count} changes were saved successfully'
 * If the smart_count is =1 we are returning: 'Changes were saved successfully'
 * If the smart_count is >1 we are returning: '%{smart_count} changes were saved successfully'
 * @param {string} message
 * @param {number} counter
 */
export function createPluralizedMessage(message, counter) {
  const phrases = {
    messageToBePrepare: message
  };
  const polyglot = new Polyglot({ locale: 'en', phrases });
  return polyglot.t('messageToBePrepare', { smart_count: counter });
}

/**
 * parser function to support predictive search
 * @param {*} x
 */
export function predictiveSearchParser(x) {
  if (x) {
    return `*${x}*`;
  }
  return '';
}
/**
 * formatter function needed after using predictiveSearchParser
 * @param {*} x
 */
export function predictiveSearchFormatter(x) {
  if (x) {
    x = x.slice(0, 1) === '*' ? x.slice(1) : x;
    x = x.slice(-1) === '*' ? x.slice(0, -1) : x;
    return x;
  }
  return '';
}

/**
 * This function will return true if value contains only numbers or characters, regardless of capitalization.
 * In every other case, the result will be false.
 * @param {string} value
 */
export function isStringAlphaNumeric(value) {
  const regAlphaNumeric = /[^A-Za-z0-9]/;

  return !regAlphaNumeric.test(value);
}

/**
 * Returns true if email parts satisfy required rules for its parts lengths.
 * @param {string} email
 */
export function isValidEmailLocalPartAndDomainName(email) {
  if (email === undefined) {
    return false;
  }

  const splitedEmail = email.split('@');

  return splitedEmail[0].length < 65 && splitedEmail[1].length < 86;
}

/**
 * This function will return true if value contains only numbers,characters, underscores or hyphen without any space regardless of capitalization.
 * In every other case, the result will be false.
 * @param {string} value
 */
export function isStringAlphaNumericUnderscoreHyphen(value) {
  const regAlphaNumericUnderscore = /[^\w-]/;

  return !regAlphaNumericUnderscore.test(value);
}

export function isStringOnlyUnderscoreHyphen(value) {
  const regUnderscoreHyphen = /[^_-]/;

  return regUnderscoreHyphen.test(value);
}

export function isValidNoneOrMultipleCommaSeparatedEmails(field) {
  if (field === undefined) {
    return true;
  }

  const regex = /^([A-Za-z0-9!#$%&'*+/=?^_\\`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_\\`{|}~-]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*(\.[A-Za-z]{2,}))([;]+[A-Za-z0-9!#$%&'*+/=?^_\\`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_\\`{|}~-]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*(\.[A-Za-z]{2,}))*$/g;

  return regex.test(field);
}

export function isValidEmailRegex(field) {
  if (field === undefined) {
    return true;
  }

  const regex = /^[A-Za-z0-9!#$%&'*+/=?^_\\`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_\\`{|}~-]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*(\.[A-Za-z]{2,})$/g;
  return regex.test(field);
}

/**
 * This function will return true if value contains numbers and characters[.()- ].
 * In every other case, the result will be false.
 * @param {string} value
 */
export function isPhoneNumberValid(value) {
  const regNumeric = /^\(?\+?[-.\s()0-9]+$/;

  return regNumeric.test(value);
}

/**
 * This function will return true if value contains numbers, characters[.()- ], has no exceed limit of 32 characters and trailing space
 * In every other case, the result will be false.
 * @param {string} value
 */
export const validatePhoneNumber = value => {
  let message = '';
  let isTrailingSpace;
  if (value) {
    isTrailingSpace = value.length === value.trim().length;

    if (!isPhoneNumberValid(value)) {
      message =
        'Phone Number can only contain numbers, plus, dot, parentheses and hyphen symbols.';
    } else if (!isTrailingSpace) {
      message = "Phone Number can't have trailing space.";
    } else if (value.length > 32) {
      message = 'Phone number should not be longer than 32 characters.';
    }
  }
  return message;
};

/**
 * This function will return true if value starts with blank(one or multiple).
 * In every other case, the result will be false.
 * @param {string} value
 */
export function isValueStartingWithBlank(value) {
  const regStringNoLeadingSpaces = /(^\s+)/;

  return regStringNoLeadingSpaces.test(value);
}

/**
 * This function will return error message if the given string value contains '<', '>' or '=' sign
 * In every other case, function will not return result
 * @param {string} value
 */
export function isStringContainingLessThanOrGreaterThanOrEqualsSign(value) {
  const regInvalidCharacters = /[<>=]/;
  if (value && regInvalidCharacters.test(value)) {
    return "Characters '<', '>' and '=' are not allowed.";
  }
}

/**
 * This function will return array of splitted string by some value
 * and each splited value will be trimed. Or null if string is null or undefined
 * @param {string} stringToDo
 * @param {string} valueToSplitBy
 */
export function splitAndTrimString(stringToDo, valueToSplitBy) {
  if (typeof stringToDo === 'string' && stringToDo !== '') {
    let splittedString = stringToDo.split(valueToSplitBy);
    splittedString.forEach(
      (element, i) => (splittedString[i] = element.trim())
    );
    return splittedString;
  }

  return null;
}

/**
 * This will return validation message if field length is longer than given param.
 * @param length
 */
export const fieldLengthValidator = length => value => {
  if (value && value.length > length) {
    return `Value should not be longer than ${length} characters.`;
  }
};

/**
 * This function will return true if value contains comma separate domains.
 * ex: "aol.com,yahoo.com"
 * @param {*} value
 */
export function isValidMultipleDomains(value) {
  const regex = /^((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))([,]+((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})))*$/g;
  return regex.test(value);
}

/**
 * This function will return error message if the value are made only by spaces
 * In every other case, function will not return result
 * @param {string} value
 */
export function isFieldContainsOnlySpaces(value) {
  if (value && !value.replace(/\s/g, '').length) {
    return `Value should not be blank`;
  }
}

export function getBodyVariables(variableType) {
  if (variableType === 'contact') {
    return contactVariables;
  } else if (variableType === 'distributionList') {
    return distributionListBodyVariables;
  } else if (variableType === 'deliveryTab') {
    return deliveryTabBodyVariables;
  }
}

export function getSubjectVariables(variableType) {
  if (variableType === 'contact') {
    return contactVariables;
  } else if (variableType === 'distributionList') {
    return distributionListSubjectVariables;
  } else if (variableType === 'deliveryTab') {
    return deliveryTabSubjectVariables;
  }
}

/**
 * Date format field validator
 * @param {*} value date field as string
 */
export const isValidDate = value => {
  if (
    value &&
    (!moment(value).isValid() ||
      !moment(value).isBetween('1900-01-01', '2079-06-06', undefined, '[]'))
  ) {
    return 'Invalid Date';
  }
};

/**
 * Date value formatter
 * @param {*} value date field as string
 */
export const formatDate = value => {
  if (value) {
    return moment(value).format('YYYY-MM-DD');
  }
};

/**
 * This function will return true if password is valid, false if not
 * Current password rules:
 * 8-15 characters long, no spaces allowed;
 * include capital letters, numbers and symbols
 * @param {string} value
 */
export function isValidPassword(value) {
  if (value === undefined) {
    return true;
  }
  if (value.includes(' ')) {
    return false;
  }
  const regex = new RegExp(
    '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[_\\W]).{8,15}$'
  );
  return regex.test(value);
}

export function equalsIgnoreCase(first, second) {
  if (
    typeof first != 'undefined' &&
    first &&
    typeof second != 'undefined' &&
    second
  ) {
    return first.toUpperCase() === second.toUpperCase();
  }
  return false;
}

/*!
 * Find the differences between two objects and push to a new object
 * https://gomakethings.com/getting-the-differences-between-two-objects-with-vanilla-js/
 */
export function diff(obj1, obj2) {
  // Make sure an object to compare is provided
  if (!obj2 || Object.prototype.toString.call(obj2) !== '[object Object]') {
    return obj1;
  }

  //
  // Variables
  //

  var diffs = {};
  var key;

  //
  // Methods
  //

  /**
   * Check if two arrays are equal
   * @param  {Array}   arr1 The first array
   * @param  {Array}   arr2 The second array
   * @return {Boolean}      If true, both arrays are equal
   */
  var arraysMatch = function(arr1, arr2) {
    // Check if the arrays are the same length
    if (arr1.length !== arr2.length) return false;

    // Check if all items exist and are in the same order
    for (var i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
        const diffObjct = diff(arr1[i], arr2[i]);
        if (Object.keys(diffObjct).length !== 0) {
          return false;
        }
      }
    }

    // Otherwise, return true
    return true;
  };

  /**
   * Compare two items and push non-matches to object
   * @param  {*}      item1 The first item
   * @param  {*}      item2 The second item
   * @param  {String} key   The key in our object
   */
  var compare = function(item1, item2, key) {
    // Get the object type
    var type1 = Object.prototype.toString.call(item1);
    var type2 = Object.prototype.toString.call(item2);

    // If type2 is undefined it has been removed
    if (type2 === '[object Undefined]') {
      diffs[key] = null;
      return;
    }

    // If items are different types
    if (type1 !== type2) {
      diffs[key] = item2;
      return;
    }

    // If an object, compare recursively
    if (type1 === '[object Object]') {
      var objDiff = diff(item1, item2);
      if (Object.keys(objDiff).length > 0) {
        diffs[key] = objDiff;
      }
      return;
    }

    // If an array, compare
    if (type1 === '[object Array]') {
      if (!arraysMatch(item1, item2)) {
        diffs[key] = item2;
      }
      return;
    }

    // Else if it's a function, convert to a string and compare
    // Otherwise, just compare
    if (type1 === '[object Function]') {
      if (item1.toString() !== item2.toString()) {
        diffs[key] = item2;
      }
    } else {
      if (item1 !== item2) {
        diffs[key] = item2;
      }
    }
  };

  //
  // Compare our objects
  //

  // Loop through the first object
  for (key in obj1) {
    compare(obj1[key], obj2[key], key);
  }

  // Loop through the second object and find missing items
  for (key in obj2) {
    if (!obj1[key] && obj1[key] !== obj2[key]) {
      diffs[key] = obj2[key];
    }
  }

  // Return the object of differences
  return diffs;
}

//queue object of promises
export class Queue {
  static queue = [];
  static stop = false;

  static enqueue(promise) {
    return new Promise((resolve, reject) => {
      this.queue.push({
        promise,
        resolve,
        reject
      });
      this.dequeue();
    });
  }

  static dequeue() {
    if (this.workingOnPromise) {
      return false;
    }
    if (this.stop) {
      this.queue = [];
      this.stop = false;
      return;
    }
    const item = this.queue.shift();
    if (!item) {
      return false;
    }
    try {
      this.workingOnPromise = true;
      item
        .promise()
        .then(value => {
          this.workingOnPromise = false;
          item.resolve(value);
          this.dequeue();
        })
        .catch(err => {
          this.workingOnPromise = false;
          item.reject(err);
          this.dequeue();
        });
    } catch (err) {
      this.workingOnPromise = false;
      item.reject(err);
      this.dequeue();
    }
    return true;
  }
}

export function getManyByChunk(
  baseUrl,
  encodedReqIds,
  paramsMaxSize = DEFAULT_CHUNK_PARAMS_MAX_SIZE
) {
  // 50 protocol and host buffer
  const idsMaxLength =
    paramsMaxSize - (50 + baseUrl.length + encodeURI('[]').length);

  const chunks = [];
  while (encodedReqIds.length > idsMaxLength) {
    const lastIndex = encodedReqIds.substring(0, idsMaxLength).lastIndexOf(',');
    chunks.push(encodedReqIds.substring(0, lastIndex));
    encodedReqIds = encodedReqIds.substring(lastIndex + 1);
  }
  chunks.push(encodedReqIds);

  // Create chunked API calls --> list of promises
  const promises = chunks.map(async ids => {
    const request = {
      url: baseUrl + encodeURI('[') + ids + encodeURI(']'),
      headers: new ApiBase().getHeaders(),
      method: 'GET'
    };

    const response = await axios.request(request);
    return response.data;
  });

  // Wait for all API calls to be completed and merge the responses into one list
  // Then return this as a promise for calling function
  return Promise.all(promises).then(responses => {
    return responses.reduce((acc, response) => {
      acc = acc.concat(response.pageData);
      return acc;
    }, []);
  });
}
